// Radha — replaces the "Pay" cell for customers whose payment_channel is
// po_portal: a large enterprise account doesn't hand a vendor its card, it
// runs the invoice through a PO / GRN / vendor-portal process on its own
// timeline. Shows that process's status instead of a Pay button that would
// misrepresent how the money actually moves. See migration 057.
import React, { useState } from "react";

import {
  useSetPaymentChannel, useSetApStatus, apProcessErrorMessage,
  type PaymentChannel, type InvoiceApStatus, type PoStatus, type GrnStatus, type PortalStatus,
} from "../hooks/useApProcess";
import { useSendInvoiceEmail, invoiceEmailErrorMessage, type DeliveryAttempt } from "../hooks/useInvoiceEmail";
import { fmtDateShort } from "../lib/money";

const PORTAL_LABEL: Record<PortalStatus, string> = {
  not_submitted: "Not submitted", submitted: "Submitted", under_review: "Under review",
  approved: "Approved", payment_scheduled: "Payment scheduled", rejected: "Rejected",
};
const PORTAL_COLOR: Record<PortalStatus, string> = {
  not_submitted: "var(--fg-muted)", submitted: "var(--status-warning)", under_review: "var(--status-warning)",
  approved: "var(--accent-purple)", payment_scheduled: "var(--status-good)", rejected: "var(--status-critical)",
};

function summarize(s: InvoiceApStatus | undefined): { text: string; color: string } {
  if (!s) return { text: "Not started", color: "var(--fg-faint)" };
  if (s.portal_status === "payment_scheduled" && s.expected_payment_date) {
    return { text: `Scheduled ${fmtDateShort(s.expected_payment_date)}`, color: PORTAL_COLOR.payment_scheduled };
  }
  if (s.po_status === "not_issued") return { text: "PO not issued", color: "var(--status-critical)" };
  if (s.grn_status !== "received") return { text: `PO issued · GRN ${s.grn_status}`, color: "var(--status-warning)" };
  return { text: PORTAL_LABEL[s.portal_status], color: PORTAL_COLOR[s.portal_status] };
}

function ApStatusEditor({ invoiceId, current, onDone }: { invoiceId: string; current?: InvoiceApStatus; onDone: () => void }) {
  const setStatus = useSetApStatus();
  const [poNumber, setPoNumber] = useState(current?.po_number ?? "");
  const [poStatus, setPoStatus] = useState<PoStatus>(current?.po_status ?? "not_issued");
  const [grnStatus, setGrnStatus] = useState<GrnStatus>(current?.grn_status ?? "pending");
  const [portalStatus, setPortalStatus] = useState<PortalStatus>(current?.portal_status ?? "not_submitted");
  const [portalRef, setPortalRef] = useState(current?.portal_reference ?? "");
  const [expDate, setExpDate] = useState(current?.expected_payment_date ?? "");
  const [note, setNote] = useState<string | null>(null);

  const save = () => {
    setNote(null);
    setStatus.mutate(
      {
        invoice_id: invoiceId, po_number: poNumber || null, po_status: poStatus, grn_status: grnStatus,
        portal_status: portalStatus, portal_reference: portalRef || null, expected_payment_date: expDate || null,
      },
      { onSuccess: onDone, onError: (e) => setNote(apProcessErrorMessage(e)) },
    );
  };

  const sel: React.CSSProperties = { padding: "2px 4px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" };
  const inp: React.CSSProperties = { ...sel, width: 90 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO number" style={inp} />
        <select value={poStatus} onChange={(e) => setPoStatus(e.target.value as PoStatus)} style={sel}>
          <option value="not_issued">PO not issued</option>
          <option value="issued">PO issued</option>
        </select>
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <select value={grnStatus} onChange={(e) => setGrnStatus(e.target.value as GrnStatus)} style={sel}>
          <option value="pending">GRN pending</option>
          <option value="partial">GRN partial</option>
          <option value="received">GRN received</option>
        </select>
        <select value={portalStatus} onChange={(e) => setPortalStatus(e.target.value as PortalStatus)} style={sel}>
          {(Object.keys(PORTAL_LABEL) as PortalStatus[]).map((k) => <option key={k} value={k}>{PORTAL_LABEL[k]}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <input value={portalRef} onChange={(e) => setPortalRef(e.target.value)} placeholder="Portal ref #" style={inp} />
        <input type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)} title="Expected payment date" style={sel} />
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <button onClick={save} disabled={setStatus.isPending}
          style={{ padding: "2px 10px", fontSize: 11, border: 0, borderRadius: 5, background: "var(--accent-purple)", color: "#fff", cursor: "pointer" }}>
          {setStatus.isPending ? "…" : "Save"}
        </button>
        <button onClick={onDone} style={{ padding: "2px 8px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>×</button>
        {note && <span style={{ fontSize: 10.5, color: "var(--status-critical)" }}>{note}</span>}
      </div>
    </div>
  );
}

export function ApStatusCell({ invoiceId, apStatus, canWrite }: { invoiceId: string; apStatus?: InvoiceApStatus; canWrite: boolean }) {
  const [editing, setEditing] = useState(false);
  if (editing) return <ApStatusEditor invoiceId={invoiceId} current={apStatus} onDone={() => setEditing(false)} />;
  const { text, color } = summarize(apStatus);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 11.5, color }}>{text}</span>
      {canWrite && (
        <button onClick={() => setEditing(true)}
          style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
          update
        </button>
      )}
    </span>
  );
}

const CHANNEL_LABEL: Record<PaymentChannel, string> = {
  direct_pay: "Card/UPI pay", invoice_email: "Email invoice", po_portal: "PO + GRN portal",
};

/** A tri-state channel picker. Selecting anything always writes an explicit
 * override (058/059) — the auto-band only applies while nobody has decided. */
export function ChannelSelect({
  customerName, effectiveChannel, isExplicit, canWrite,
}: { customerName: string; effectiveChannel: PaymentChannel; isExplicit: boolean; canWrite: boolean }) {
  const setChannel = useSetPaymentChannel();
  if (!canWrite) {
    return isExplicit ? null : <span style={{ fontSize: 10, color: "var(--fg-faint)" }}>(auto)</span>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <select
        value={effectiveChannel}
        onChange={(e) => setChannel.mutate({ customer_name: customerName, payment_channel: e.target.value as PaymentChannel })}
        disabled={setChannel.isPending}
        title="How this customer actually pays — overrides the size-based default"
        style={{ padding: "1px 4px", fontSize: 10, border: "1px dashed var(--border)", borderRadius: 5, background: "none", color: "var(--fg-faint)", cursor: "pointer" }}>
        {(Object.keys(CHANNEL_LABEL) as PaymentChannel[]).map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
      </select>
      {!isExplicit && <span style={{ fontSize: 10, color: "var(--fg-faint)" }}>(auto)</span>}
    </span>
  );
}

function summarizeEmail(attempts: DeliveryAttempt[]): { text: string; color: string } {
  if (attempts.length === 0) return { text: "Not sent", color: "var(--fg-faint)" };
  const last = attempts[0];
  const when = last.sent_at ? fmtDateShort(last.sent_at) : "—";
  if (last.status === "failed") return { text: `Failed ${when}`, color: "var(--status-critical)" };
  return { text: `Emailed ${when}`, color: "var(--status-good)" };
}

function EmailInvoiceEditor({ invoiceId, onDone }: { invoiceId: string; onDone: () => void }) {
  const send = useSendInvoiceEmail();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const go = () => {
    setNote(null);
    send.mutate({ invoiceId, recipientEmail: email.trim() }, {
      onSuccess: () => { setNote({ ok: true, text: "Sent" }); setTimeout(onDone, 900); },
      onError: (e) => setNote({ ok: false, text: invoiceEmailErrorMessage(e) }),
    });
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
      <input type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} placeholder="customer@company.com" autoFocus
        style={{ width: 150, padding: "2px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }} />
      <button onClick={go} disabled={send.isPending || !email.trim()}
        style={{ padding: "1px 8px", fontSize: 11, border: 0, borderRadius: 5, background: "var(--accent-purple)", color: "#fff", cursor: "pointer" }}>
        {send.isPending ? "…" : "Send"}
      </button>
      <button onClick={onDone} style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>×</button>
      {note && <span style={{ fontSize: 10.5, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
    </span>
  );
}

export function EmailInvoiceCell({ invoiceId, attempts, canWrite }: { invoiceId: string; attempts: DeliveryAttempt[]; canWrite: boolean }) {
  const [editing, setEditing] = useState(false);
  if (editing) return <EmailInvoiceEditor invoiceId={invoiceId} onDone={() => setEditing(false)} />;
  const { text, color } = summarizeEmail(attempts);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 11.5, color }}>{text}</span>
      {canWrite && (
        <button onClick={() => setEditing(true)}
          style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
          email
        </button>
      )}
    </span>
  );
}
